import styled from "styled-components";

import { OpenInButtons } from "@pages/AdminDashboard/OpenInButtons";
import { orderStatusLabel, shippingMethodLabel } from "@pages/AdminDashboard/plainLanguageLabels";
import { describeShipAddress } from "@pages/AdminDashboard/ShipScreen/shipAddressFormat";
import { useIsPhoneLayout } from "@pages/AdminDashboard/theme/AdminLayout";
import { adminTokens } from "@pages/AdminDashboard/theme/adminTokens";
import { CopyButton } from "@pages/AdminDashboard/ui/CopyButton";
import { shortenIdentifier } from "@pages/AdminDashboard/ui/DataTable/cells";
import { StatusBadge } from "@pages/AdminDashboard/ui/StatusBadge";
import { Case } from "@types-runway/case";

import { AccountOrder, countAccount } from "./accountOrders";
import { OrderTracking } from "./OrderTracking";

const { color, radius, shadow, typeScale } = adminTokens;
const [, , , spaceLg, , spaceXl] = adminTokens.spacing;
const cardPaddingY = 18;
const cardGap = 28;
const noValue = "—";

// One row that wraps on a narrow window; on a phone the parts stand in one column (ACCT-R27).
const Region = styled.section<{ $isPhone: boolean }>`
  display: flex;
  flex-direction: ${({ $isPhone }) => ($isPhone ? "column" : "row")};
  align-items: ${({ $isPhone }) => ($isPhone ? "stretch" : "center")};
  flex-wrap: wrap;
  gap: ${({ $isPhone }) => ($isPhone ? spaceLg : cardGap)}px;
  box-sizing: border-box;
  padding: ${cardPaddingY}px ${({ $isPhone }) => ($isPhone ? spaceLg : spaceXl)}px;
  background-color: ${color.surface};
  border: 1px solid ${color.border};
  border-radius: ${radius.card}px;
  box-shadow: ${shadow.card};
  color: ${color.navy};
  font-family: ${typeScale.body.fontFamily};
  font-size: ${typeScale.body.fontSize};
`;

const Stack = styled.div<{ $gap: number; $alignEnd?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: ${({ $alignEnd }) => ($alignEnd ? "flex-end" : "flex-start")};
  gap: ${({ $gap }) => $gap}px;
  min-width: 0;
`;

const Eyebrow = styled.span`
  color: ${color.inkSoft};
  font-family: ${typeScale.eyebrow.fontFamily};
  font-size: ${typeScale.eyebrow.fontSize};
  letter-spacing: ${typeScale.eyebrow.letterSpacing};
  text-transform: uppercase;
`;

const IdRow = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
`;

const OrderId = styled.span`
  font-family: ${typeScale.labelValue.fontFamily};
  font-size: 16px;
`;

const Meta = styled.span`
  color: ${color.inkSoft};
  font-family: ${typeScale.meta.fontFamily};
  font-size: ${typeScale.meta.fontSize};
`;

const Fields = styled.dl`
  display: contents;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const Label = styled.dt`
  color: ${color.inkSoft};
  font-family: ${typeScale.meta.fontFamily};
  font-size: ${typeScale.meta.fontSize};
`;

const Value = styled.dd`
  margin: 0;
  font-family: ${typeScale.labelValue.fontFamily};
  overflow-wrap: anywhere;

  a {
    font-size: inherit;
  }
`;

const Spacer = styled.div`
  flex: 1;
`;

const ActsOn = styled(Meta)`
  strong {
    color: ${color.navy};
    font-family: ${typeScale.labelValue.fontFamily};
    font-weight: normal;
  }
`;

interface LatestOrderCardProps {
  latest: AccountOrder;
  cases: Case[];
}

// The User's latest Order with what Redo and Ship act on (ACCT-R3…R8). Its date line is
// ENG-1064's (ORDERS-R25).
export const LatestOrderCard = ({ latest: { order, caseRecord }, cases }: LatestOrderCardProps) => {
  const isPhone = useIsPhoneLayout();
  const shortId = shortenIdentifier(order.order_id);
  const address = caseRecord.shippingAddress;

  return (
    <Region $isPhone={isPhone} aria-label="Latest order">
      <Stack $gap={2}>
        <Eyebrow>Latest order</Eyebrow>
        <IdRow>
          <OrderId title={order.order_id}>{shortId}</OrderId>
          <CopyButton value={order.order_id} what="order ID" />
        </IdRow>
        <Meta>{`#${order.id} · Case #${caseRecord.id}`}</Meta>
      </Stack>
      <Stack $gap={4}>
        <StatusBadge status={order.status} label={orderStatusLabel(order.status)} />
      </Stack>
      <Fields>
        <Field>
          <Label>Shipping method</Label>
          <Value>
            {order.shipping_method ? shippingMethodLabel(order.shipping_method) : noValue}
          </Value>
        </Field>
        <Field>
          <Label>Tracking</Label>
          <Value>
            <OrderTracking order={order} />
          </Value>
        </Field>
        <Field>
          <Label>Ships to</Label>
          <Value>{address ? describeShipAddress(address) : noValue}</Value>
        </Field>
      </Fields>
      {!isPhone && <Spacer />}
      <Stack $gap={6} $alignEnd={!isPhone}>
        <ActsOn>
          Acts on <strong>{shortId}</strong>
        </ActsOn>
        <OpenInButtons caseId={caseRecord.caseId} subject={shortId} />
        <Meta>{countAccount(cases)}</Meta>
      </Stack>
    </Region>
  );
};
